import {
  Body,
  Button,
  ColumnHeaderCell,
  Header,
  Row,
  Table,
  Typography,
  useToast,
} from "@hellocarbo/lasagne";
import { Plus, Wind } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import { SimulationRow } from "@/components/Simulation";
import Card from "@/components/ui/Card";
import PageWrapper from "@/components/ui/PageWrapper";
import { TSimulation } from "@/types";

export async function loader() {
  const response = await fetch("/api/client/simulations");

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: simulations } = (await response.json()) as {
    data: Array<TSimulation>;
  };

  return { ok: true, simulations };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const simulationId = await formData.get("simulationId");

  invariant(simulationId, "No simulation id found.");

  const response = await fetch(`/api/client/simulations/${simulationId}`, {
    headers: { "Content-Type": "application/json" },
    method: "DELETE",
  });

  if (response.status !== 204) {
    return { ok: false };
  }

  return { ok: true };
}

function Simulations() {
  const { simulations } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();

  const { t } = useTranslation("simulation");
  const { toast } = useToast();
  const submit = useSubmit();

  useEffect(() => {
    if (navigation.state === "idle" && actionData?.ok) {
      toast({
        title: t("simulation:simulationDeleted"),
        toastType: "fancy",
        intent: "positive",
      });
    }
  }, [navigation.state, actionData]);

  return (
    <PageWrapper>
      <div className="mx-xl my-lg">
        <Typography step="3" as="h1" weight="bold" className="mb-md">
          {t("simulation:simulations")}
        </Typography>
        {simulations && simulations.length > 0 ? (
          <Card>
            <Button
              intent="positive"
              shape="soft"
              size="small"
              as={Link}
              to="/simulations/scenarios"
            >
              <Plus className="size-sm" />
              {t("simulation:newSimulation")}
            </Button>
            <Table className="mt-md">
              <Header>
                <Row>
                  <ColumnHeaderCell>
                    {t("simulation:simulations")}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell>
                    {t("simulation:employeesNumber")}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell>
                    {t("simulation:emissions")}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell className="text-right">
                    {t("simulation:actions")}
                  </ColumnHeaderCell>
                </Row>
              </Header>
              <Body>
                {simulations.map((simulation) => (
                  <SimulationRow
                    key={simulation.id}
                    simulation={simulation}
                    deleteSimulation={() =>
                      submit(
                        { simulationId: simulation.id },
                        { method: "DELETE" }
                      )
                    }
                  />
                ))}
              </Body>
            </Table>
          </Card>
        ) : (
          <Card className="mt-sm text-center">
            <Wind className="size-md mx-auto" />
            <Typography as="h3" step="1" weight="bold" className="my-2xs">
              {t("simulation:noSimulation")}
            </Typography>
            <Typography as="p" step="-1" className="mb-sm text-grayscale">
              {t("simulation:noSimulationMessage")}
            </Typography>
            <Button
              intent="positive"
              shape="soft"
              as={Link}
              to="/simulations/scenarios"
            >
              <Plus className="size-md" />
              {t("simulation:newSimulation")}
            </Button>
          </Card>
        )}
      </div>
    </PageWrapper>
  );
}

export default Simulations;
